import { DocumentType } from '@synergy/models';
import { PropsWithChildren, ReactElement, createContext, useContext, useEffect, useRef, useState } from 'react';
import { getUrlSegment } from '@synergy/utils';
import { useParams } from 'react-router-dom';

import { ConvertOutcome } from '../convert-receipt/models/convert-outcome';
import { DocumentChainStep } from '../../common/models/document-chain-step';
import { Transition } from '../convert-receipt/models/type-transition';
import { TransitionSource } from '../convert-receipt/models/type-transition-source';
import { TransitionTarget } from '../convert-receipt/models/internal/transition-target';
import { useThunkDispatch } from '../../store/use-thunk-dispatch';

/**
 * What the mask lends the chain so a follow-up can be made from it (#2166).
 *
 * The tab knows which stage is missing; only the mask holds the document the
 * conversion starts from and the handler that carries it out.
 */
export interface DocumentChainCreate {
    /**
     * The document the conversion starts from. Absent until it is there — the
     * demand fetches its conversion data only when someone asks for it, and
     * then `prepare` is what asks.
     */
    source?: TransitionSource;
    /** What this document can become, when the source cannot answer it yet. */
    targets?: TransitionTarget[];
    /** Fetches the source; called once, when the reader picks a follow-up. */
    prepare?: () => void;
    onConvert: (
        transition: Transition,
        target: TransitionTarget,
        source: DocumentType
    ) => void | Promise<ConvertOutcome | void>;
}

export interface DocumentChainState {
    steps: DocumentChainStep[];
    loading: boolean;
    /** The document the mask is showing, as the chain names it. */
    currentKey?: string;
    /** The same document as the endpoints name it — the chain stream needs both. */
    documentType?: DocumentType;
    documentId?: number;
    create?: DocumentChainCreate;
    registerCreate?: (create: DocumentChainCreate | undefined) => void;
}

const PaloDocumentChainContext = createContext<DocumentChainState>({ steps: [], loading: false });

export const useDocumentChain = (): DocumentChainState => useContext(PaloDocumentChainContext);

/**
 * Holds the procurement chain of the open document for the whole mask (#2160).
 *
 * The chain is asked for once and read in two places — the tab shows how many
 * documents there are, the tab's content shows them — so it cannot live in
 * either of them. It is deliberately not in the store: it belongs to one open
 * document, and nothing outside this mask has a use for it.
 *
 * The id comes from the route rather than from the mask, because the masks are
 * class components that cannot pass it into a hook, and every document route is
 * "/<type>/<id>" — the same segment the endpoint takes.
 */
export const PaloDocumentChainProvider = ({
    documentType,
    children,
}: PropsWithChildren<{ documentType: DocumentType }>): ReactElement => {
    const dispatch = useThunkDispatch();
    const params = useParams();
    const [state, setState] = useState<DocumentChainState>({ steps: [], loading: false });
    // The provider wraps the mask from the outside, so it cannot reach the
    // document or the conversion handler itself — the mask hands them in from
    // within, through PaloDocumentChainCreate.
    const [create, setCreate] = useState<DocumentChainCreate | undefined>(undefined);

    // A document being created has no id yet, and nothing to be part of.
    const documentId = Number(params['id']);
    const known = Number.isInteger(documentId) && documentId > 0;

    useEffect(() => {
        if (!known) {
            setState({ steps: [], loading: false });
            return;
        }

        let current = true;

        setState(previous => ({ ...previous, loading: true }));

        void dispatch((_dispatch, _getState, { api }) =>
            api.transitionActivities.getDocumentChain(documentType, documentId)
        ).then(response => {
            if (!current) {
                return;
            }

            setState({
                steps: response.success && response.result ? response.result : [],
                loading: false,
            });
        });

        return () => {
            current = false;
        };
    }, [dispatch, documentId, documentType, known]);

    // A credit note lives in the invoice route, and the chain folds it onto
    // the invoice it is — so both sides name it the same way.
    const currentKey = known ? `${getUrlSegment(documentType)}:${documentId}` : undefined;

    return (
        <PaloDocumentChainContext.Provider
            value={{
                ...state,
                currentKey,
                documentType,
                documentId: known ? documentId : undefined,
                create,
                registerCreate: setCreate,
            }}
        >
            {children}
        </PaloDocumentChainContext.Provider>
    );
};

/**
 * Lends the chain the document and the handler a follow-up is made with.
 *
 * Renders nothing; the masks are class components and cannot register through
 * a hook of their own.
 */
export const PaloDocumentChainCreate = ({
    source,
    targets,
    prepare,
    onConvert,
}: DocumentChainCreate): ReactElement | null => {
    const { registerCreate } = useDocumentChain();

    // The masks that hand this in are of both kinds: a class component passes
    // the same handler every time, a function component builds a new one on
    // every render. Registering on identity would loop for the second sort —
    // each registration renders again, which builds another handler. So what is
    // registered stays the same object and reads the current values from here.
    const latest = useRef({ source, targets, prepare, onConvert });
    latest.current = { source, targets, prepare, onConvert };

    // What actually changes anything: which document, and whether its source has
    // arrived — a demand fetches that only once someone asks for a follow-up.
    const key = `${(source as { id?: number } | undefined)?.id ?? ''}:${source ? 'ready' : 'pending'}:${
        targets?.join(',') ?? ''
    }`;

    useEffect(() => {
        registerCreate?.({
            source: latest.current.source,
            targets: latest.current.targets,
            prepare: () => latest.current.prepare?.(),
            onConvert: (transition, target, documentType) => latest.current.onConvert(transition, target, documentType),
        });

        return () => registerCreate?.(undefined);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [key, registerCreate]);

    return null;
};
